import { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const normalizeComboOffer = (item = {}) => ({
  combo_id: String(item?.combo_id ?? item?.id ?? ""),
  combo_name: String(item?.combo_name ?? ""),
  combo_price: String(item?.combo_price ?? ""),
  combo_description: String(item?.combo_description ?? ""),
  combo_type: String(item?.combo_type ?? ""),
  is_configurable: String(item?.is_configurable ?? ""),
  combo_image: String(item?.combo_image ?? item?.image ?? ""),
  is_active: String(item?.is_active ?? ""),
});

const normalizeOptionGroupItem = (item = {}) => ({
  pk_option_group_item_id: String(item?.pk_option_group_item_id ?? ""),
  fk_combo_id: String(item?.fk_combo_id ?? item?.combo_id ?? ""),
  item_selection_name: String(item?.item_selection_name ?? ""),
  minimum_selection: String(item?.minimum_selection ?? "0"),
  maximum_selection: String(item?.maximum_selection ?? "0"),
  is_item_configurable: String(
    item?.is_item_configurable ?? item?.is_configurable ?? "N",
  ),
  is_configurable: String(
    item?.is_item_configurable ?? item?.is_configurable ?? "N",
  ),
  is_active: String(item?.is_active ?? "Y"),
  items: Array.isArray(item?.items) ? item.items : [],
});

export default function useCombo() {
  const { api, anlookApi, userID } = useContext(GlobalContext);

  // Prevent duplicate requests caused by StrictMode,
  // fast double-clicks, or overlapping UI calls.
  const comboInsertPromiseRef = useRef(null);
  const comboFetchPromiseRef = useRef(new Map());
  const optionGroupInsertPromiseRef = useRef(null);
  const optionGroupItemsFetchPromiseRef = useRef(new Map());
  const comboConfigurableInsertPromiseRef = useRef(null);
  const catalogV3FetchPromiseRef = useRef(null);
  const imageUploadPromiseRef = useRef(null);
  const comboOfferUpdatePromiseRef = useRef(null);
  const optionGroupUpdatePromiseRef = useRef(new Map());
  const comboConfigurableUpdatePromiseRef = useRef(new Map());

  // ================= COMBO OFFER =================

  const [comboOffers, setComboOffers] = useState([]);
  const [comboOffersLoading, setComboOffersLoading] = useState(false);
  const [comboOfferSaving, setComboOfferSaving] = useState(false);
  const [comboOfferUpdating, setComboOfferUpdating] = useState(false);
  const [bhandareImageUploading, setBhandareImageUploading] = useState(false);

  // ================= OPTION GROUP ITEMS =================

  // Stored combo-wise:
  const [optionGroupItems, setOptionGroupItems] = useState({});
  const [optionGroupItemsLoading, setOptionGroupItemsLoading] = useState({});
  const [optionGroupSaving, setOptionGroupSaving] = useState(false);
  const [optionGroupUpdating, setOptionGroupUpdating] = useState(false);

  // ================= CONFIGURABLE COMBO ITEMS =================
  const [comboConfigurableSaving, setComboConfigurableSaving] = useState(false);
  const [comboConfigurableUpdating, setComboConfigurableUpdating] =
    useState(false);

  // ================= CATALOG V3 =================
  const [catalogV3Loading, setCatalogV3Loading] = useState(false);

  const cleanImageUrl = (value) =>
    String(value || "")
      .trim()
      .replace(/^['"]+|['"]+$/g, "")
      .replace(/\\\//g, "/");

  // =====================================================
  // UPLOAD COMBO IMAGE
  // Same uploader used by Bhandare Wala image flow.
  // =====================================================

  const uploadComboImage = async ({
    item_name,
    image_data,
    type = "IMG",
  } = {}) => {
    if (imageUploadPromiseRef.current) {
      return imageUploadPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!anlookApi) {
          toast.error("Anlook API instance missing");
          return null;
        }

        if (!image_data) {
          toast.error("Please select image");
          return null;
        }

        const finalImageData = String(image_data || "");

        if (!finalImageData.startsWith("data:image/")) {
          toast.error("Invalid image data");
          return null;
        }

        const dataSeparatorIndex = finalImageData.indexOf(",");

        if (dataSeparatorIndex === -1) {
          toast.error("Invalid image base64 data");
          return null;
        }

        const metadata = finalImageData.slice(0, dataSeparatorIndex);

        if (!metadata.includes(";base64")) {
          toast.error("Image must be base64 encoded");
          return null;
        }

        const base64 = finalImageData.slice(dataSeparatorIndex + 1);

        if (!base64) {
          toast.error("Image data is empty");
          return null;
        }

        const dataUrlMimeMatch = finalImageData.match(/^data:([^;,]+)[;,]/i);
        const dataUrlMime = String(dataUrlMimeMatch?.[1] || "").toLowerCase();

        // Send the original selected image Data URL exactly as received.
        // No conversion to WEBP (or any other format) is performed.
        const allowedMimeTypes = new Set([
          "image/png",
          "image/jpeg",
          "image/jpg",
          "image/webp",
        ]);

        if (!allowedMimeTypes.has(dataUrlMime)) {
          toast.error("Only PNG, JPG, JPEG and WEBP images are allowed");
          return null;
        }

        setBhandareImageUploading(true);

        console.log("COMBO IMAGE UPLOAD:", {
          item_name,
          type: "IMG",
          mimeFromData: dataUrlMime,
          originalFormatPreserved: true,
          dataLength: finalImageData.length,
        });

        const res = await anlookApi.post("/v1/uploadItemImagev2.php", {
          userid: String(userID || "A1437"),
          item_name: String(item_name || `combo_${Date.now()}`),
          type: "IMG",
          image_data: finalImageData,
        });

        if (String(res?.data?.status) === "0" && res?.data?.image_url) {
          return {
            ...res.data,
            image_url: cleanImageUrl(res.data.image_url),
          };
        }

        toast.error(
          res?.data?.message ||
            res?.data?.responseMessage ||
            "Image upload failed",
        );

        return null;
      } catch (err) {
        console.error("Combo image upload error:", err?.response?.data || err);

        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.responseMessage ||
            err?.message ||
            "Image upload failed",
        );

        return null;
      } finally {
        setBhandareImageUploading(false);
      }
    })();

    imageUploadPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (imageUploadPromiseRef.current === requestPromise) {
        imageUploadPromiseRef.current = null;
      }
    }
  };

  // Backward-compatible alias if you want the same function name elsewhere.
  const uploadBhandareWalaImage = uploadComboImage;

  // =====================================================
  // INSERT COMBO OFFER
  // =====================================================

  const insertComboOffer = async (payload = {}) => {
    if (comboInsertPromiseRef.current) {
      return comboInsertPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        setComboOfferSaving(true);

        const requestPayload = {
          combo_name: String(payload?.combo_name ?? "").trim(),
          combo_price: String(payload?.combo_price ?? "").trim(),
          combo_description: String(payload?.combo_description ?? "").trim(),
          combo_image: String(payload?.combo_image ?? "").trim(),
          combo_type: String(payload?.combo_type ?? "").trim(),
          is_configurable: String(payload?.is_configurable ?? "N").trim(),
          is_active: String(payload?.is_active ?? "Y").trim(),
        };

        if (!requestPayload.combo_name) {
          toast.error("Combo name is required");
          return false;
        }

        const res = await api.post("/Order/insertComboOffer", requestPayload);

        if (res?.data?.responseCode === "00") {
          toast.success(
            res?.data?.responseMessage || "Combo offer added successfully",
          );

          return true;
        }

        toast.error(res?.data?.responseMessage || "Failed to add combo offer");

        return false;
      } catch (err) {
        console.error("Insert combo offer error:", err?.response?.data || err);

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error adding combo offer",
        );

        return false;
      } finally {
        setComboOfferSaving(false);
      }
    })();

    comboInsertPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (comboInsertPromiseRef.current === requestPromise) {
        comboInsertPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // UPDATE COMBO OFFER
  // =====================================================

  const updateComboOffer = async (payload = {}) => {
    if (comboOfferUpdatePromiseRef.current) {
      return comboOfferUpdatePromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        const requestPayload = {
          combo_id: String(payload?.combo_id ?? payload?.comboId ?? "").trim(),
          combo_name: String(payload?.combo_name ?? "").trim(),
          combo_price: String(payload?.combo_price ?? "").trim(),
          combo_description: String(payload?.combo_description ?? "").trim(),
          combo_image: String(payload?.combo_image ?? "").trim(),
          is_active: String(payload?.is_active ?? "Y").trim(),
        };

        if (!requestPayload.combo_id) {
          toast.error("Combo ID is required");
          return false;
        }

        if (!requestPayload.combo_name) {
          toast.error("Combo name is required");
          return false;
        }

        setComboOfferUpdating(true);

        const res = await api.post("/Order/updateComboOffer", requestPayload, {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        });

        if (res?.data?.responseCode === "00") {
          toast.success(
            res?.data?.responseMessage || "Combo updated successfully",
          );
          return true;
        }

        toast.error(res?.data?.responseMessage || "Failed to update combo");
        return false;
      } catch (err) {
        console.error("Update combo offer error:", err?.response?.data || err);
        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error updating combo",
        );
        return false;
      } finally {
        setComboOfferUpdating(false);
      }
    })();

    comboOfferUpdatePromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (comboOfferUpdatePromiseRef.current === requestPromise) {
        comboOfferUpdatePromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // GET COMBO OFFER
  // =====================================================

  const getComboOffer = async (comboName = "", options = {}) => {
    const finalComboName = String(comboName ?? "").trim();
    const requestKey = finalComboName.toLowerCase();
    const forceRefresh = options?.force === true;

    if (!(comboFetchPromiseRef.current instanceof Map)) {
      comboFetchPromiseRef.current = new Map();
    }

    const existingPromise = comboFetchPromiseRef.current.get(requestKey);

    if (existingPromise && !forceRefresh) {
      return existingPromise;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return [];
        }

        setComboOffersLoading(true);

        const requestPayload = {
          combo_name: finalComboName,
        };

        const res = await api.post("/Order/getComboOffer", requestPayload, {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        });

        if (res?.data?.responseCode === "00") {
          const responseData =
            res?.data?.response || res?.data?.data || res?.data?.result || [];

          const finalData = Array.isArray(responseData)
            ? responseData.map(normalizeComboOffer)
            : [];

          setComboOffers(finalData);

          return finalData;
        }

        setComboOffers([]);

        toast.error(
          res?.data?.responseMessage || "Failed to fetch combo offers",
        );

        return [];
      } catch (err) {
        console.error("Get combo offer error:", err?.response?.data || err);

        setComboOffers([]);

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching combo offers",
        );

        return [];
      } finally {
        setComboOffersLoading(false);
      }
    })();

    if (!(comboFetchPromiseRef.current instanceof Map)) {
      comboFetchPromiseRef.current = new Map();
    }

    comboFetchPromiseRef.current.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (
        comboFetchPromiseRef.current instanceof Map &&
        comboFetchPromiseRef.current.get(requestKey) === requestPromise
      ) {
        comboFetchPromiseRef.current.delete(requestKey);
      }
    }
  };

  // =====================================================
  // GET COMBO OFFER - SAME API ALIAS.
  // =====================================================

  const fetchComboOffers = async (comboName = "") => {
    return getComboOffer(comboName);
  };

  // =====================================================
  // INSERT COMBO CONFIGURABLE
  // =====================================================

  const insertComboConfigurable = async (payload = {}) => {
    if (comboConfigurableInsertPromiseRef.current) {
      return comboConfigurableInsertPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        const finalUserId = String(
          payload?.user_id ??
            payload?.userid ??
            payload?.userId ??
            userID ??
            "",
        ).trim();

        const finalComboId = String(
          payload?.combo_id ?? payload?.comboId ?? "",
        ).trim();

        const finalOptionGroupItemId = String(
          payload?.option_group_item_id ??
            payload?.optionGroupItemId ??
            payload?.optionGroupId ??
            "",
        ).trim();

        const rawItemIds =
          payload?.item_id ?? payload?.itemId ?? payload?.itemIds ?? [];

        const finalItemIds = (
          Array.isArray(rawItemIds) ? rawItemIds : [rawItemIds]
        )
          .map((id) => String(id ?? "").trim())
          .filter(Boolean);

        if (!finalUserId) {
          toast.error("User ID is required");
          return false;
        }

        if (!finalComboId) {
          toast.error("Combo ID is required");
          return false;
        }

        if (!finalOptionGroupItemId) {
          toast.error("Option group item ID is required");
          return false;
        }

        if (!finalItemIds.length) {
          toast.error("Please select at least one item");
          return false;
        }

        setComboConfigurableSaving(true);

        const requestPayload = {
          user_id: finalUserId,
          combo_id: finalComboId,
          option_group_item_id: finalOptionGroupItemId,
          item_id: finalItemIds,
        };

        const res = await api.post(
          "/Order/insertComboConfigurable",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          return true;
        }

        toast.error(
          res?.data?.responseMessage ||
            "Failed to insert combo configurable items",
        );

        return false;
      } catch (err) {
        console.error(
          "Insert combo configurable error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error inserting combo configurable items",
        );

        return false;
      } finally {
        setComboConfigurableSaving(false);
      }
    })();

    comboConfigurableInsertPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (comboConfigurableInsertPromiseRef.current === requestPromise) {
        comboConfigurableInsertPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // UPDATE COMBO CONFIGURABLE ITEM STATUS
  // =====================================================

  const updateComboConfigurable = async (payload = {}) => {
    const finalComboId = String(
      payload?.combo_id ?? payload?.comboId ?? "",
    ).trim();
    const finalItemId = String(
      payload?.item_id ?? payload?.itemId ?? "",
    ).trim();
    const finalIsActive = String(payload?.is_active ?? "Y").trim();
    const requestKey = `${finalComboId}:${finalItemId}`;

    if (!finalComboId) {
      toast.error("Combo ID is required");
      return false;
    }

    if (!finalItemId) {
      toast.error("Item ID is required");
      return false;
    }

    const existingPromise =
      comboConfigurableUpdatePromiseRef.current.get(requestKey);

    if (existingPromise) return existingPromise;

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        setComboConfigurableUpdating(true);

        const res = await api.post(
          "/Order/updateComboConfigurable",
          {
            combo_id: finalComboId,
            item_id: finalItemId,
            is_active: finalIsActive,
          },
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        if (res?.data?.responseCode === "00") {
          return true;
        }

        toast.error(
          res?.data?.responseMessage || "Failed to update combo item",
        );
        return false;
      } catch (err) {
        console.error(
          "Update combo configurable error:",
          err?.response?.data || err,
        );
        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error updating combo item",
        );
        return false;
      } finally {
        setComboConfigurableUpdating(false);
      }
    })();

    comboConfigurableUpdatePromiseRef.current.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (
        comboConfigurableUpdatePromiseRef.current.get(requestKey) ===
        requestPromise
      ) {
        comboConfigurableUpdatePromiseRef.current.delete(requestKey);
      }
    }
  };

  // =====================================================
  // GET CATALOG ITEMS V3
  // =====================================================

  const getCatalogItemsV3 = async (payload = {}) => {
    if (catalogV3FetchPromiseRef.current) {
      return catalogV3FetchPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return null;
        }

        const requestPayload = {
          userid: String(
            payload?.userid ??
              payload?.user_id ??
              payload?.userId ??
              userID ??
              "",
          ).trim(),
          orderType: String(
            payload?.orderType ?? payload?.order_type ?? "takeaway",
          ).trim(),
          platform: String(payload?.platform ?? "pos").trim(),
        };

        if (!requestPayload.userid) {
          toast.error("User ID is required");
          return null;
        }

        setCatalogV3Loading(true);

        const res = await api.post(
          "/Order/v3.0/getCatalogItems",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          return (
            res?.data?.response ?? res?.data?.data ?? res?.data?.result ?? null
          );
        }

        toast.error(
          res?.data?.responseMessage || "Failed to fetch v3 catalogue",
        );

        return null;
      } catch (err) {
        console.error(
          "Get catalog items v3 error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching v3 catalogue",
        );

        return null;
      } finally {
        setCatalogV3Loading(false);
      }
    })();

    catalogV3FetchPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (catalogV3FetchPromiseRef.current === requestPromise) {
        catalogV3FetchPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // INSERT COMBO OPTION GROUP DETAILS
  // =====================================================

  const insertComboOptionGroupDetails = async (payload = {}) => {
    if (optionGroupInsertPromiseRef.current) {
      return optionGroupInsertPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        const requestPayload = {
          fkComboId: String(
            payload?.fkComboId ?? payload?.fk_combo_id ?? "",
          ).trim(),
          itemSelectionName: String(
            payload?.itemSelectionName ?? payload?.item_selection_name ?? "",
          ).trim(),
          minimumSelection: String(
            payload?.minimumSelection ?? payload?.minimum_selection ?? "0",
          ).trim(),
          maximumSelection: String(
            payload?.maximumSelection ?? payload?.maximum_selection ?? "0",
          ).trim(),
          isConfigurable: String(
            payload?.isConfigurable ?? payload?.is_configurable ?? "N",
          ).trim(),
        };

        if (!requestPayload.fkComboId) {
          toast.error("Combo ID is required");
          return false;
        }

        if (!requestPayload.itemSelectionName) {
          toast.error("Item selection name is required");
          return false;
        }

        const minSelection = Number(requestPayload.minimumSelection);
        const maxSelection = Number(requestPayload.maximumSelection);

        if (
          !Number.isFinite(minSelection) ||
          !Number.isFinite(maxSelection) ||
          minSelection < 0 ||
          maxSelection < 0
        ) {
          toast.error("Minimum and maximum selection must be valid numbers");
          return false;
        }

        if (maxSelection < minSelection) {
          toast.error(
            "Maximum selection cannot be less than minimum selection",
          );
          return false;
        }

        setOptionGroupSaving(true);

        const res = await api.post(
          "/Order/insertComboOptionGroupDetails",
          requestPayload,
        );

        console.log("INSERT COMBO OPTION GROUP DETAILS RESPONSE:", res?.data);

        if (res?.data?.responseCode === "00") {
          // toast.success(
          //   res?.data?.responseMessage || "Option group added successfully",
          // );

          return true;
        }

        toast.error(res?.data?.responseMessage || "Failed to add option group");

        return false;
      } catch (err) {
        console.error(
          "Insert combo option group details error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error adding option group",
        );

        return false;
      } finally {
        setOptionGroupSaving(false);
      }
    })();

    optionGroupInsertPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (optionGroupInsertPromiseRef.current === requestPromise) {
        optionGroupInsertPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // UPDATE COMBO OPTION GROUP STATUS
  // =====================================================

  const updateComboOptionGroupItems = async (payload = {}) => {
    const finalOptionGroupItemId = String(
      payload?.option_group_item_id ??
        payload?.optionGroupItemId ??
        payload?.id ??
        "",
    ).trim();
    const finalIsActive = String(payload?.is_active ?? "Y").trim();

    if (!finalOptionGroupItemId) {
      toast.error("Option group item ID is required");
      return false;
    }

    const existingPromise = optionGroupUpdatePromiseRef.current.get(
      finalOptionGroupItemId,
    );

    if (existingPromise) return existingPromise;

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        setOptionGroupUpdating(true);

        const res = await api.post(
          "/Order/updateComboOptionGroupItems",
          {
            option_group_item_id: finalOptionGroupItemId,
            is_active: finalIsActive,
          },
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        if (res?.data?.responseCode === "00") {
          return true;
        }

        toast.error(
          res?.data?.responseMessage || "Failed to update option group",
        );
        return false;
      } catch (err) {
        console.error(
          "Update combo option group error:",
          err?.response?.data || err,
        );
        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error updating option group",
        );
        return false;
      } finally {
        setOptionGroupUpdating(false);
      }
    })();

    optionGroupUpdatePromiseRef.current.set(
      finalOptionGroupItemId,
      requestPromise,
    );

    try {
      return await requestPromise;
    } finally {
      if (
        optionGroupUpdatePromiseRef.current.get(finalOptionGroupItemId) ===
        requestPromise
      ) {
        optionGroupUpdatePromiseRef.current.delete(finalOptionGroupItemId);
      }
    }
  };

  // =====================================================
  // GET OPTION GROUP ITEMS
  // =====================================================

  const getOptionGroupItems = async (comboId) => {
    const finalComboId = String(comboId ?? "").trim();

    if (!finalComboId) {
      toast.error("Combo ID is required");
      return [];
    }

    const existingPromise =
      optionGroupItemsFetchPromiseRef.current.get(finalComboId);

    if (existingPromise) {
      return existingPromise;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return [];
        }

        setOptionGroupItemsLoading((prev) => ({
          ...prev,
          [finalComboId]: true,
        }));

        const requestPayload = {
          combo_id: finalComboId,
        };

        const res = await api.post(
          "/Order/getOptionGroupItems",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          const responseData =
            res?.data?.response || res?.data?.data || res?.data?.result || [];

          const normalizedData = Array.isArray(responseData)
            ? responseData.map(normalizeOptionGroupItem)
            : [];

          const groupedByComboId = normalizedData.reduce((acc, item) => {
            const comboId = String(item?.fk_combo_id ?? "").trim();

            if (!comboId) return acc;

            if (!acc[comboId]) {
              acc[comboId] = [];
            }

            acc[comboId].push(item);
            return acc;
          }, {});

          const selectedComboItems = groupedByComboId[finalComboId] || [];

          setOptionGroupItems((prev) => ({
            ...prev,
            ...groupedByComboId,
            [finalComboId]: selectedComboItems,
          }));

          return selectedComboItems;
        }

        setOptionGroupItems((prev) => ({
          ...prev,
          [finalComboId]: [],
        }));

        toast.error(
          res?.data?.responseMessage || "Failed to fetch option group items",
        );

        return [];
      } catch (err) {
        console.error(
          "Get option group items error:",
          err?.response?.data || err,
        );

        setOptionGroupItems((prev) => ({
          ...prev,
          [finalComboId]: [],
        }));

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching option group items",
        );

        return [];
      } finally {
        setOptionGroupItemsLoading((prev) => ({
          ...prev,
          [finalComboId]: false,
        }));
      }
    })();

    optionGroupItemsFetchPromiseRef.current.set(finalComboId, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (
        optionGroupItemsFetchPromiseRef.current.get(finalComboId) ===
        requestPromise
      ) {
        optionGroupItemsFetchPromiseRef.current.delete(finalComboId);
      }
    }
  };

  return {
    // ================= COMBO OFFERS =================
    comboOffers,
    comboOffersLoading,
    comboOfferSaving,
    comboOfferUpdating,
    bhandareImageUploading,

    insertComboOffer,
    updateComboOffer,
    uploadComboImage,
    uploadBhandareWalaImage,
    getComboOffer,
    fetchComboOffers,

    // ================= CONFIGURABLE COMBO ITEMS =================
    comboConfigurableSaving,
    comboConfigurableUpdating,
    insertComboConfigurable,
    updateComboConfigurable,

    // ================= CATALOG V3 =================
    catalogV3Loading,
    getCatalogItemsV3,

    // ================= OPTION GROUP ITEMS =================
    optionGroupItems,
    optionGroupItemsLoading,
    optionGroupSaving,
    optionGroupUpdating,
    insertComboOptionGroupDetails,
    updateComboOptionGroupItems,
    getOptionGroupItems,
  };
}
